import { useCallback, useEffect, useState } from 'react';
import type { EnhancedMessageTrackingData } from '../services/outreach/types';
import { fetchEngagementSnapshots } from '../services/outreach/engagementAnalyticsService';
import {
  EngagementTimeRange,
  getDaysFromRange,
  getClickedTimestamp,
  getDeliveredTimestamp,
  getOpenedTimestamp,
  isWithinRange,
  normalizeRecipient,
  roundToTwo,
} from '../utils/engagementAnalytics';

const DEFAULT_RANGE: EngagementTimeRange = '30d';
const PERCENT_TOLERANCE = 1; // Threshold to consider change stable

export interface EngagementMetricsData {
  totalOpens: number;
  uniqueOpens: number;
  totalClicks: number;
  uniqueClicks: number;
  engagedRecipients: number;
  engagementPercentage: number;
  avgOpenRate: number;
  avgClickRate: number;
  avgEngagementRate: number;
  engagementRate: number;
  clickToOpenRate: number;
  avgOpensPerRecipient: number;
  avgClicksPerRecipient: number;
  avgTimeToFirstOpen: number;
  uniqueOpensChange: string;
  openRateChange: string;
  clickRateChange: string;
  clickToOpenChange: string;
  engagementRateChange: string;
  engagedRecipientsChange: string;
  avgOpensChange: string;
  timeToOpenChange: string;
  uniqueOpensTrend: TrendDirection;
  openRateTrend: TrendDirection;
  clickRateTrend: TrendDirection;
  clickToOpenTrend: TrendDirection;
  engagementRateTrend: TrendDirection;
  engagedRecipientsTrend: TrendDirection;
  avgOpensTrend: TrendDirection;
  timeToOpenTrend: TrendDirection;
}

type TrendDirection = 'up' | 'down' | 'stable';

interface BaseMetrics {
  totalOpens: number;
  uniqueOpens: number;
  totalClicks: number;
  uniqueClicks: number;
  engagedRecipients: number;
  engagementPercentage: number;
  avgOpenRate: number;
  avgClickRate: number;
  avgEngagementRate: number;
  clickToOpenRate: number;
  avgOpensPerRecipient: number;
  avgClicksPerRecipient: number;
  avgTimeToFirstOpen: number;
}

const defaultMetrics: BaseMetrics = {
  totalOpens: 0,
  uniqueOpens: 0,
  totalClicks: 0,
  uniqueClicks: 0,
  engagedRecipients: 0,
  engagementPercentage: 0,
  avgOpenRate: 0,
  avgClickRate: 0,
  avgEngagementRate: 0,
  clickToOpenRate: 0,
  avgOpensPerRecipient: 0,
  avgClicksPerRecipient: 0,
  avgTimeToFirstOpen: 0,
};

const buildMetrics = (
  records: EnhancedMessageTrackingData[],
  start: number,
  end: number
): BaseMetrics => {
  if (!records.length) {
    return defaultMetrics;
  }

  const uniqueRecipients = new Set<string>();
  const openedRecipients = new Set<string>();
  const clickedRecipients = new Set<string>();
  const engagedRecipients = new Set<string>();

  let deliveredCount = 0;
  let totalOpens = 0;
  let totalClicks = 0;
  const timeDiffs: number[] = [];

  records.forEach((record) => {
    const deliveredTimestamp = getDeliveredTimestamp(record);
    if (!isWithinRange(deliveredTimestamp, start, end)) {
      return;
    }

    const recipient = normalizeRecipient(record.recipient, record.id);
    uniqueRecipients.add(recipient);

    if (deliveredTimestamp) {
      deliveredCount += 1;
    }

    const openedTimestamp = getOpenedTimestamp(record);
    if (isWithinRange(openedTimestamp, start, end)) {
      totalOpens += 1;
      openedRecipients.add(recipient);
      engagedRecipients.add(recipient);

      if (deliveredTimestamp && openedTimestamp) {
        const diff = openedTimestamp - deliveredTimestamp;
        if (diff >= 0) {
          timeDiffs.push(diff);
        }
      }
    }

    const clickedTimestamp = getClickedTimestamp(record);
    if (isWithinRange(clickedTimestamp, start, end)) {
      totalClicks += 1;
      clickedRecipients.add(recipient);
      engagedRecipients.add(recipient);
    }
  });

  const totalRecipients = uniqueRecipients.size;
  const openRecipientCount = openedRecipients.size;
  const clickRecipientCount = clickedRecipients.size;

  const avgTimeToFirstOpen =
    timeDiffs.length > 0
      ? roundToTwo(
          timeDiffs.reduce((sum, diff) => sum + diff, 0) /
            timeDiffs.length /
            1000
        )
      : 0;

  const avgOpenRate =
    deliveredCount > 0 ? roundToTwo((openRecipientCount / deliveredCount) * 100) : 0;
  const avgClickRate =
    deliveredCount > 0 ? roundToTwo((clickRecipientCount / deliveredCount) * 100) : 0;
  const avgEngagementRate =
    deliveredCount > 0
      ? roundToTwo((engagedRecipients.size / deliveredCount) * 100)
      : 0;
  const clickToOpenRate =
    openRecipientCount > 0 ? roundToTwo((clickRecipientCount / openRecipientCount) * 100) : 0;

  return {
    totalOpens,
    uniqueOpens: openRecipientCount,
    totalClicks,
    uniqueClicks: clickRecipientCount,
    engagedRecipients: engagedRecipients.size,
    engagementPercentage:
      totalRecipients > 0
        ? roundToTwo((engagedRecipients.size / totalRecipients) * 100)
        : 0,
    avgOpenRate,
    avgClickRate,
    avgEngagementRate,
    clickToOpenRate,
    avgOpensPerRecipient:
      openRecipientCount > 0
        ? roundToTwo(totalOpens / openRecipientCount)
        : 0,
    avgClicksPerRecipient:
      clickRecipientCount > 0
        ? roundToTwo(totalClicks / clickRecipientCount)
        : 0,
    avgTimeToFirstOpen,
  };
};

const calculateChange = (
  current: number,
  previous: number,
  options: { reverse?: boolean } = {}
): { change: string; trend: TrendDirection } => {
  if (!Number.isFinite(previous) || previous === 0) {
    if (current === 0) {
      return { change: '0%', trend: 'stable' };
    }
    return { change: '+100%', trend: options.reverse ? 'down' : 'up' };
  }

  const percentChange = ((current - previous) / Math.abs(previous)) * 100;
  const formatted = `${percentChange >= 0 ? '+' : ''}${roundToTwo(
    percentChange
  )}%`;

  let trend: TrendDirection = 'stable';
  if (Math.abs(percentChange) >= PERCENT_TOLERANCE) {
    trend = percentChange > 0 ? 'up' : 'down';
  }

  if (options.reverse && trend !== 'stable') {
    trend = trend === 'up' ? 'down' : 'up';
  }

  return { change: formatted, trend };
};

const buildChangeSet = (
  current: BaseMetrics,
  previous: BaseMetrics
): Pick<
  EngagementMetricsData,
  | 'uniqueOpensChange'
  | 'uniqueOpensTrend'
  | 'openRateChange'
  | 'openRateTrend'
  | 'clickRateChange'
  | 'clickRateTrend'
  | 'clickToOpenChange'
  | 'clickToOpenTrend'
  | 'engagementRateChange'
  | 'engagementRateTrend'
  | 'engagedRecipientsChange'
  | 'engagedRecipientsTrend'
  | 'avgOpensChange'
  | 'avgOpensTrend'
  | 'timeToOpenChange'
  | 'timeToOpenTrend'
> => {
  const uniqueOpens = calculateChange(
    current.uniqueOpens,
    previous.uniqueOpens
  );
  const openRate = calculateChange(current.avgOpenRate, previous.avgOpenRate);
  const clickRate = calculateChange(
    current.avgClickRate,
    previous.avgClickRate
  );
  const clickToOpen = calculateChange(
    current.clickToOpenRate,
    previous.clickToOpenRate
  );
  const engagementRate = calculateChange(
    current.avgEngagementRate,
    previous.avgEngagementRate
  );
  const engaged = calculateChange(
    current.engagedRecipients,
    previous.engagedRecipients
  );
  const avgOpens = calculateChange(
    current.avgOpensPerRecipient,
    previous.avgOpensPerRecipient
  );
  const timeToOpen = calculateChange(
    current.avgTimeToFirstOpen,
    previous.avgTimeToFirstOpen,
    { reverse: true }
  );

  return {
    uniqueOpensChange: uniqueOpens.change,
    uniqueOpensTrend: uniqueOpens.trend,
    openRateChange: openRate.change,
    openRateTrend: openRate.trend,
    clickRateChange: clickRate.change,
    clickRateTrend: clickRate.trend,
    clickToOpenChange: clickToOpen.change,
    clickToOpenTrend: clickToOpen.trend,
    engagementRateChange: engagementRate.change,
    engagementRateTrend: engagementRate.trend,
    engagedRecipientsChange: engaged.change,
    engagedRecipientsTrend: engaged.trend,
    avgOpensChange: avgOpens.change,
    avgOpensTrend: avgOpens.trend,
    timeToOpenChange: timeToOpen.change,
    timeToOpenTrend: timeToOpen.trend,
  };
};

export const useEngagementMetrics = (
  timeRange: EngagementTimeRange = DEFAULT_RANGE
) => {
  const [data, setData] = useState<EngagementMetricsData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMetrics = useCallback(
    async (forceRefresh = false) => {
      setIsLoading(true);
      setError(null);

      try {
        const days = getDaysFromRange(timeRange);
        const now = Date.now();
        const currentStart = now - days * 24 * 60 * 60 * 1000;
        const previousStart = currentStart - days * 24 * 60 * 60 * 1000;

        const snapshots = await fetchEngagementSnapshots({
          includeStats: false,
          includeTracking: true,
          forceRefresh,
        });

        const allRecords = snapshots.flatMap(
          (snapshot) => snapshot.tracking ?? []
        );
        const currentMetrics = buildMetrics(allRecords, currentStart, now);
        const previousMetrics = buildMetrics(
          allRecords,
          previousStart,
          currentStart
        );
        const changes = buildChangeSet(currentMetrics, previousMetrics);

        setData({
          ...currentMetrics,
          engagementRate: currentMetrics.avgEngagementRate,
          ...changes,
        });
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('Unable to load engagement metrics')
        );
      } finally {
        setIsLoading(false);
      }
    },
    [timeRange]
  );

  const refreshMetrics = useCallback(() => fetchMetrics(true), [fetchMetrics]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    data,
    isLoading,
    error,
    refresh: refreshMetrics,
  };
};
